import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  parsePhoneNumber,
  CountryCode,
} from 'libphonenumber-js';
import { Metadata } from 'libphonenumber-js/core';
import {
  isValidPhoneNumber as isValidMobileNumber,
  parsePhoneNumberFromString as parseMobileNumber,
  getCountries as getMobileCountries,
} from 'libphonenumber-js/mobile';
import examples from 'libphonenumber-js/mobile/examples';
import metadata from 'libphonenumber-js/mobile/metadata';

export interface CountryPhoneOption {
  label: string;
  name: string;
  value: CountryCode;
  dial: string;
  nationalLength: number;
}

type RegionDisplayNames = { of(code: string): string | undefined };

const regionNames: RegionDisplayNames | null =
  typeof Intl !== 'undefined' && (Intl as any).DisplayNames
    ? new (Intl as any).DisplayNames(['en'], { type: 'region' })
    : null;

export function getCountryName(iso: string): string {
  try {
    return regionNames?.of(iso) || iso;
  } catch {
    return iso;
  }
}

const nationalLengthCache: Partial<Record<CountryCode, number>> = {};
const PRIMARY_COUNTRY_BY_CALLING_CODE: Record<string, CountryCode> = {
  '1': 'US',
  '7': 'RU',
  '44': 'GB',
};

let callingCodeIndex: { code: string; countries: CountryCode[] }[] | null = null;

function getCallingCodeIndex(): { code: string; countries: CountryCode[] }[] {
  if (callingCodeIndex) {
    return callingCodeIndex;
  }
  const byCode: Record<string, CountryCode[]> = {};
  getCountries().forEach((iso) => {
    const code = String(getCountryCallingCode(iso));
    if (!byCode[code]) {
      byCode[code] = [];
    }
    byCode[code].push(iso);
  });
  callingCodeIndex = Object.keys(byCode)
    .sort((a, b) => b.length - a.length)
    .map((code) => ({ code, countries: byCode[code] }));
  return callingCodeIndex;
}

export function getNationalLength(iso: CountryCode): number {
  if (nationalLengthCache[iso] != null) {
    return nationalLengthCache[iso] as number;
  }
  let length = 10;
  try {
    const example = getExampleNumber(iso, examples);
    if (example?.nationalNumber) {
      length = example.nationalNumber.length;
    }
  } catch {
    // fall through
  }
  nationalLengthCache[iso] = length;
  return length;
}

/** National length, or +1 only while the value starts with a trunk 0. */
export function getExpectedNationalLength(iso: CountryCode, digits = ''): number {
  const limit = getNationalLength(iso);
  return String(digits || '').startsWith('0') ? Math.min(limit + 1, 16) : limit;
}

/** Input maxlength follows the first digit: 10, or 11 if it starts with 0. */
export function getNationalInputMaxLength(iso: CountryCode, digits = ''): number {
  return getExpectedNationalLength(iso, digits);
}

export function getCountryLabel(iso: CountryCode): string {
  return `${getCountryName(iso)} (+${getCountryCallingCode(iso)})`;
}

export function getPhoneLimitError(iso: CountryCode, requiredEmpty = false, digits = ''): string {
  const limit = getExpectedNationalLength(iso, digits);
  const label = getCountryLabel(iso);
  if (requiredEmpty) {
    return `Mobile number is required. Required length: ${limit} digits for ${label}.`;
  }
  return `Phone number is not valid. Required length: ${limit} digits for ${label}.`;
}

export function getCountryMismatchError(iso: CountryCode, belongsToIso?: CountryCode): string {
  const selected = getCountryLabel(iso);
  if (belongsToIso && belongsToIso !== iso) {
    return `This number belongs to ${getCountryLabel(belongsToIso)}. It does not match ${selected}.`;
  }
  return `This number does not match ${selected}. Select the country this number belongs to.`;
}

export function buildCountryOptions(): CountryPhoneOption[] {
  return getCountries()
    .map((iso) => ({
      name: getCountryName(iso),
      label: getCountryLabel(iso),
      value: iso,
      dial: `+${getCountryCallingCode(iso)}`,
      nationalLength: getNationalLength(iso),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function normalizePhoneDigits(raw: string): string {
  return String(raw || '').replace(/[^\d+]/g, '');
}

/** GB/GG/JE/IM share +44; NANP countries share +1, etc. */
export function callingCodesMatch(a?: CountryCode | null, b?: CountryCode | null): boolean {
  if (!a || !b) {
    return false;
  }
  if (a === b) {
    return true;
  }
  try {
    return getCountryCallingCode(a) === getCountryCallingCode(b);
  } catch {
    return false;
  }
}

export function matchesSelectedCountryLength(digits: string, iso: CountryCode): boolean {
  const value = String(digits || '');
  return value.length === getExpectedNationalLength(iso, value);
}

export function nationalToE164(digits: string, iso: CountryCode): string {
  const limit = getNationalLength(iso);
  let national = String(digits || '').replace(/\D/g, '');
  if (national.length === limit + 1 && national.startsWith('0')) {
    national = national.slice(1);
  }
  try {
    const parsed =
      parsePhoneNumberFromString(digits, iso) || parsePhoneNumberFromString(national, iso);
    if (parsed && callingCodesMatch(parsed.country, iso)) {
      return parsed.number;
    }
  } catch {
    // fall through
  }
  return `+${getCountryCallingCode(iso)}${national}`;
}

function isPlausibleNationalNumber(national: string, iso: CountryCode): boolean {
  if (!national) {
    return false;
  }
  const limit = getNationalLength(iso);
  const len = national.length;
  if (len === limit) {
    return true;
  }
  if (national.startsWith('0') && len === limit + 1) {
    return true;
  }
  // Variable-length countries (e.g. DE): allow a small range around the example length.
  return len >= Math.max(6, limit - 3) && len <= Math.min(15, limit + 3);
}

function pickCountryForCallingCode(
  callingCode: string,
  fallbackIso: CountryCode,
  parsedCountry?: CountryCode | null
): CountryCode {
  if (parsedCountry && callingCodesMatch(parsedCountry, fallbackIso)) {
    return fallbackIso;
  }
  if (parsedCountry) {
    return parsedCountry;
  }
  try {
    if (String(getCountryCallingCode(fallbackIso)) === String(callingCode)) {
      return fallbackIso;
    }
  } catch {
    // ignore
  }
  const primary = PRIMARY_COUNTRY_BY_CALLING_CODE[callingCode];
  if (primary) {
    return primary;
  }
  const match = getCallingCodeIndex().find((item) => item.code === callingCode);
  return match?.countries[0] || fallbackIso;
}

function fromParsedInternational(
  parsed: ReturnType<typeof parsePhoneNumberFromString>,
  fallbackIso: CountryCode
): { countryIso: CountryCode; nationalDigits: string } | null {
  if (!parsed?.nationalNumber || !parsed.countryCallingCode) {
    return null;
  }
  return {
    countryIso: pickCountryForCallingCode(
      String(parsed.countryCallingCode),
      fallbackIso,
      parsed.country
    ),
    nationalDigits: parsed.nationalNumber,
  };
}

/** Drop selected dial code (e.g. 92 / 44 / 971) when remaining digits match that country. */
export function stripSelectedCallingCode(digits: string, iso: CountryCode): string {
  const value = String(digits || '').replace(/\D/g, '');
  if (!value) {
    return '';
  }
  let callingCode = '';
  try {
    callingCode = String(getCountryCallingCode(iso));
  } catch {
    return value;
  }
  if (!callingCode || !value.startsWith(callingCode)) {
    return value;
  }
  const rest = value.slice(callingCode.length);
  if (isPlausibleNationalNumber(rest, iso)) {
    return rest;
  }
  return value;
}

/**
 * Detect any country calling code (longest match) and return national digits.
 * Used when paste includes +44 / +971 / 0091 / 1415... etc.
 */
export function matchAnyCallingCode(
  digits: string,
  fallbackIso: CountryCode,
  explicitIntl = false
): { countryIso: CountryCode; nationalDigits: string } | null {
  const value = String(digits || '').replace(/\D/g, '');
  if (!value) {
    return null;
  }
  const expectedLen = getExpectedNationalLength(fallbackIso, value);
  // Local-length numbers must not be treated as another country's dial code
  // (e.g. PK 3437068981 starts with Spain +34).
  if (!explicitIntl && value.length <= expectedLen) {
    const stripped = stripSelectedCallingCode(value, fallbackIso);
    if (stripped !== value) {
      return { countryIso: fallbackIso, nationalDigits: stripped };
    }
    return null;
  }

  try {
    const selectedCode = String(getCountryCallingCode(fallbackIso));
    if (value.startsWith(selectedCode)) {
      const rest = value.slice(selectedCode.length);
      if (isPlausibleNationalNumber(rest, fallbackIso)) {
        return { countryIso: fallbackIso, nationalDigits: rest };
      }
    }
  } catch {
    // ignore
  }

  for (const { code, countries } of getCallingCodeIndex()) {
    if (!value.startsWith(code)) {
      continue;
    }
    const rest = value.slice(code.length);
    const country = countries.includes(fallbackIso)
      ? fallbackIso
      : pickCountryForCallingCode(code, fallbackIso);
    if (isPlausibleNationalNumber(rest, country)) {
      return { countryIso: country, nationalDigits: rest };
    }
  }
  return null;
}

/** Canonical countries where this national number is a valid mobile. */
export function findCanonicalMobileCountries(digits: string): CountryCode[] {
  const value = String(digits || '').replace(/\D/g, '');
  if (value.length < 6) {
    return [];
  }
  const seen = new Set<string>();
  const matches: CountryCode[] = [];
  for (const iso of getMobileCountries()) {
    try {
      if (!isValidMobileNumber(value, iso)) {
        continue;
      }
      const parsed = parseMobileNumber(value, iso);
      const canonical = (parsed?.country || iso) as CountryCode;
      if (!seen.has(canonical)) {
        seen.add(canonical);
        matches.push(canonical);
      }
    } catch {
      // ignore
    }
  }
  return matches;
}

let mobileMetadata: Metadata | null = null;
const leadingDigitCache: Partial<Record<CountryCode, string[] | null>> = {};

function getMobileMetadata(): Metadata {
  if (!mobileMetadata) {
    mobileMetadata = new Metadata(metadata);
  }
  return mobileMetadata;
}

function splitTopLevelAlts(pattern: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];
    if (char === '(') {
      depth++;
    } else if (char === ')') {
      depth--;
    }
    if (char === '|' && depth === 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  if (current) {
    parts.push(current);
  }
  return parts;
}

function unwrapOuterGroup(pattern: string): string {
  if (!pattern.startsWith('(?:') && !pattern.startsWith('(')) {
    return pattern;
  }
  let depth = 0;
  const start = pattern.startsWith('(?:') ? 3 : 1;
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '(') {
      depth++;
    } else if (pattern[i] === ')') {
      depth--;
      if (depth === 0) {
        return pattern.slice(start, i);
      }
    }
  }
  return pattern;
}

function expandCharClass(cls: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < cls.length; i++) {
    if (cls[i] === '\\') {
      i++;
      continue;
    }
    if (cls[i + 1] === '-' && cls[i + 2] && /\d/.test(cls[i]) && /\d/.test(cls[i + 2])) {
      const from = cls.charCodeAt(i);
      const to = cls.charCodeAt(i + 2);
      for (let code = from; code <= to; code++) {
        out.push(String.fromCharCode(code));
      }
      i += 2;
      continue;
    }
    if (/\d/.test(cls[i])) {
      out.push(cls[i]);
    }
  }
  return out;
}

function firstDigitsOfAlt(alt: string): string[] | null {
  let value = alt.trim();
  while (value.startsWith('(?:') || (value.startsWith('(') && value[1] !== '[')) {
    value = value.startsWith('(?:') ? value.slice(3) : value.slice(1);
  }
  if (/^\d/.test(value)) {
    return [value[0]];
  }
  if (value[0] === '[') {
    const end = value.indexOf(']');
    if (end < 1) {
      return null;
    }
    const expanded = expandCharClass(value.slice(1, end));
    return expanded.length ? expanded : null;
  }
  return null;
}

function getMobileLeadingDigits(iso: CountryCode): string[] | null {
  if (iso in leadingDigitCache) {
    return leadingDigitCache[iso] as string[] | null;
  }
  try {
    const meta = getMobileMetadata();
    meta.selectNumberingPlan(iso);
    const numberingPlan = meta.numberingPlan as { type?: (kind: string) => { pattern?: () => string } } | undefined;
    const type = numberingPlan?.type?.('MOBILE');
    const pattern = type?.pattern?.();
    if (!pattern) {
      leadingDigitCache[iso] = null;
      return null;
    }
    const unwrapped = unwrapOuterGroup(String(pattern));
    const digits = new Set<string>();
    for (const alt of splitTopLevelAlts(unwrapped)) {
      const found = firstDigitsOfAlt(alt);
      if (!found) {
        leadingDigitCache[iso] = null;
        return null;
      }
      found.forEach((digit) => digits.add(digit));
    }
    const list = Array.from(digits);
    leadingDigitCache[iso] = list.length ? list : null;
    return leadingDigitCache[iso] as string[] | null;
  } catch {
    leadingDigitCache[iso] = null;
    return null;
  }
}

function examplePrefixScore(digits: string, iso: CountryCode): number {
  try {
    const example = getExampleNumber(iso, examples)?.nationalNumber || '';
    const value = String(digits || '').replace(/^0+/, '');
    let matched = 0;
    const limit = Math.min(4, example.length, value.length);
    while (matched < limit && example[matched] === value[matched]) {
      matched++;
    }
    const lengthBonus = getNationalLength(iso) === value.length ? 20 : 0;
    const lead = getMobileLeadingDigits(iso);
    const specific = lead && lead.length > 0 && lead.length <= 3 ? 3 : 0;
    return matched * 10 + lengthBonus + specific;
  } catch {
    return 0;
  }
}

function couldBeTypingSelectedCountry(digits: string, iso: CountryCode): boolean {
  const value = String(digits || '').replace(/\D/g, '');
  if (!value) {
    return true;
  }
  if (matchesSelectedCountryLength(value, iso) || isValidMobileNumber(value, iso)) {
    return false;
  }
  const expected = getExpectedNationalLength(iso, value);
  if (value.length >= expected) {
    return false;
  }
  const lead = getMobileLeadingDigits(iso);
  if (!lead || !lead.length) {
    return true;
  }
  const national = value.replace(/^0+/, '') || value;
  return lead.indexOf(national[0]) !== -1;
}

function pickBestMobileCountry(
  digits: string,
  matches: CountryCode[],
  selectedIso: CountryCode,
  preferredIso?: CountryCode
): CountryCode | null {
  const others = matches.filter(
    (iso) => iso !== selectedIso && !callingCodesMatch(iso, selectedIso)
  );
  if (!others.length) {
    return null;
  }
  if (others.length === 1) {
    return others[0];
  }
  others.sort((a, b) => {
    const scoreDiff = examplePrefixScore(digits, b) - examplePrefixScore(digits, a);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    if (preferredIso && preferredIso !== selectedIso) {
      if (a === preferredIso) {
        return -1;
      }
      if (b === preferredIso) {
        return 1;
      }
    }
    const leadA = getMobileLeadingDigits(a)?.length || 9;
    const leadB = getMobileLeadingDigits(b)?.length || 9;
    return leadA - leadB;
  });
  return others[0];
}

/**
 * Keep the selected country. If the digits are a mobile from another country,
 * return an error that names that country — for every country, not only PK/IN.
 */
export function resolveNationalMobileCountry(
  digits: string,
  selectedIso: CountryCode,
  preferredIso?: CountryCode,
  _allowCountrySwitch = true
): { countryIso: CountryCode; switched: boolean; error?: string } {
  const value = String(digits || '').replace(/\D/g, '');
  if (value.length < 6) {
    return { countryIso: selectedIso, switched: false };
  }

  try {
    if (isValidMobileNumber(value, selectedIso)) {
      return { countryIso: selectedIso, switched: false };
    }
  } catch {
    // fall through
  }

  const matches = findCanonicalMobileCountries(value);
  const belongsToIso = pickBestMobileCountry(value, matches, selectedIso, preferredIso);
  if (!belongsToIso) {
    return { countryIso: selectedIso, switched: false };
  }

  if (couldBeTypingSelectedCountry(value, selectedIso)) {
    return { countryIso: selectedIso, switched: false };
  }

  return {
    countryIso: selectedIso,
    switched: false,
    error: getCountryMismatchError(selectedIso, belongsToIso),
  };
}

export function parseExistingPhone(
  value: string,
  fallbackIso: CountryCode = 'PK',
  preferredIso?: CountryCode
): {
  countryIso: CountryCode;
  nationalDigits: string;
} {
  let trimmed = (value || '').trim();
  if (!trimmed) {
    return { countryIso: fallbackIso, nationalDigits: '' };
  }
  // International prefix 00... is the same as +...
  if (trimmed.startsWith('00')) {
    trimmed = `+${trimmed.slice(2)}`;
  }
  const explicitIntl = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  const nationalLen = getNationalLength(fallbackIso);
  const expectedLen = getExpectedNationalLength(fallbackIso, digits);
  try {
    if (explicitIntl) {
      const parsed = fromParsedInternational(parsePhoneNumberFromString(trimmed), fallbackIso);
      if (parsed) {
        return parsed;
      }
    }

    // Only treat as a local number when length matches and it is a mobile for that country.
    if (
      matchesSelectedCountryLength(digits, fallbackIso) &&
      isValidMobileNumber(digits, fallbackIso)
    ) {
      const asNational = parseMobileNumber(digits, fallbackIso) || parsePhoneNumber(digits, fallbackIso);
      if (asNational && callingCodesMatch(asNational.country, fallbackIso)) {
        return { countryIso: fallbackIso, nationalDigits: asNational.nationalNumber };
      }
    }

    // Pasted/stored values often include the calling code without '+', e.g. 923437068981.
    const looksIntl = digits.length > expectedLen && !digits.startsWith('0');
    if (looksIntl || digits.length > nationalLen + 1 || explicitIntl) {
      const parsedIntl = fromParsedInternational(
        parsePhoneNumberFromString(explicitIntl ? trimmed : `+${digits}`),
        fallbackIso
      );
      if (parsedIntl) {
        return parsedIntl;
      }
    }

    const matched = matchAnyCallingCode(digits, fallbackIso, explicitIntl);
    if (matched) {
      return matched;
    }

    if (matchesSelectedCountryLength(digits, fallbackIso)) {
      const resolved = resolveNationalMobileCountry(
        digits,
        fallbackIso,
        preferredIso || fallbackIso,
        true
      );
      if (resolved.switched) {
        const parsed = parseMobileNumber(digits, resolved.countryIso);
        return {
          countryIso: resolved.countryIso,
          nationalDigits: parsed?.nationalNumber || digits,
        };
      }
    }
  } catch {
    // ignore
  }
  return {
    countryIso: fallbackIso,
    nationalDigits: digits,
  };
}

export function validateNationalPhone(
  input: string,
  countryIso: CountryCode,
  required = true,
  preferredIso?: CountryCode,
  allowCountrySwitch = true
): { valid: boolean; e164?: string; detectedCountry?: CountryCode; error?: string } {
  const raw = String(input || '').trim();
  const digits = raw.replace(/\D/g, '');

  if (!digits) {
    if (!required) {
      return { valid: true };
    }
    return { valid: false, error: getPhoneLimitError(countryIso, true) };
  }

  try {
    // Only treat '+' as international. Never prefix national digits with '+' —
    // that steals the first digits as another country's calling code (UK 7… → +7 Russia).
    if (raw.startsWith('+')) {
      const parsedIntl = parsePhoneNumberFromString(raw);
      if (parsedIntl?.isValid() && parsedIntl.country) {
        if (callingCodesMatch(parsedIntl.country, countryIso)) {
          return {
            valid: true,
            e164: parsedIntl.number,
            detectedCountry: countryIso,
          };
        }
        return {
          valid: false,
          error: getCountryMismatchError(countryIso, parsedIntl.country),
          detectedCountry: countryIso,
        };
      }
    }
  } catch {
    // fall through
  }

  const resolved = resolveNationalMobileCountry(
    digits,
    countryIso,
    preferredIso,
    allowCountrySwitch
  );
  if (resolved.error) {
    return { valid: false, error: resolved.error, detectedCountry: countryIso };
  }

  if (matchesSelectedCountryLength(digits, countryIso)) {
    try {
      const mobile = parseMobileNumber(digits, countryIso);
      if (mobile?.isValid()) {
        return { valid: true, e164: mobile.number, detectedCountry: countryIso };
      }
    } catch {
      // fall through to length-based e164
    }
    // Gym forms still accept a complete local number when it is not a known mobile elsewhere.
    return {
      valid: true,
      e164: nationalToE164(digits, countryIso),
      detectedCountry: countryIso,
    };
  }

  return { valid: false, error: getPhoneLimitError(countryIso, false, digits) };
}
